import React from "react";

/**
 * One rendered Suggestion card. All strings (title, action,
 * availability summary) are SERVER-RENDERED — this component just
 * draws boxes and chips, it never authors text.
 *
 * Pill rules (live-hardening update):
 *   - kind="combo"  + availability.all_in_stock=true  → "In stock" (green)
 *   - kind="combo"  + availability.all_in_stock=false → "Check availability" (amber)
 *   - kind="generic"                                  → "Quick action" (neutral)
 *
 * Confidence is still emitted by the server and used as a data
 * attribute (data-confidence) for analytics-ready introspection, but
 * is NOT surfaced as user-facing copy — "High match" / "Good match"
 * / "Worth a try" on generic action cards was misleading.
 *
 * Props
 *   card: {
 *     kind:         "combo" | "generic",
 *     title:        string,
 *     action:       string,
 *     items:        Array<{role,product_id,name,source,qty_in_store}>,
 *     availability: { summary, all_in_stock, in_inventory_count,
 *                     catalog_only_count } | null,
 *     confidence:   "high" | "medium" | "low",
 *   }
 *   index: zero-based card index (used for testids)
 */
function ComboAvailabilityPill({ availability }) {
  if (!availability) return null;
  const inStock = availability.all_in_stock === true;
  const label = inStock ? "In stock" : "Check availability";
  const cls = inStock
    ? "bg-shelf-fresh-bg text-shelf-fresh"
    : "bg-shelf-attention-bg text-shelf-attention";
  return (
    <span
      data-testid="suggestion-availability"
      data-state={inStock ? "in_stock" : "check_availability"}
      className={`shrink-0 inline-flex items-center px-2 py-0.5 rounded-full font-mono text-[10px] uppercase tracking-wider ${cls}`}
    >
      {label}
    </span>
  );
}

function GenericPill() {
  return (
    <span
      data-testid="suggestion-quick-action"
      data-state="quick_action"
      className="shrink-0 inline-flex items-center px-2 py-0.5 rounded-full font-mono text-[10px] uppercase tracking-wider bg-secondary text-ink/70"
    >
      Quick action
    </span>
  );
}

export function SuggestionCard({ card, index }) {
  if (!card) return null;
  const isCombo = card.kind === "combo";

  return (
    <div
      data-testid={`suggestion-card-${index}`}
      data-kind={card.kind}
      data-confidence={card.confidence}
      className="border border-border rounded-sm bg-paper p-4 space-y-3"
    >
      <div className="flex flex-col items-start gap-2">
        <h3
          className="font-display font-black text-base leading-snug text-ink"
          data-testid={`suggestion-title-${index}`}
        >
          {card.title}
        </h3>
        {isCombo
          ? <ComboAvailabilityPill availability={card.availability} />
          : <GenericPill />}
      </div>

      {isCombo && Array.isArray(card.items) && card.items.length > 0 && (
        <ul className="flex flex-wrap gap-1.5" data-testid={`suggestion-items-${index}`}>
          {card.items.map((it) => (
            <li
              key={`${it.role}-${it.product_id}`}
              data-testid={`suggestion-item-${index}-${it.role}`}
              data-source={it.source}
              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-sm bg-secondary font-mono text-[11px] text-ink/80"
            >
              <span className="opacity-60 uppercase tracking-wider text-[9px]">
                {it.role.replace("_pair", "")}
              </span>
              <span className="font-semibold" title={it.name}>{it.display_name || it.name}</span>
              {it.source === "catalog" && (
                <span className="opacity-60">· {it.catalog_scope === "shared" ? "Shared catalog" : "Store catalog"} — check availability</span>
              )}
              {typeof it.qty_in_store === "number" && it.qty_in_store > 0 && (
                <span className="opacity-60">× {it.qty_in_store}</span>
              )}
            </li>
          ))}
        </ul>
      )}

      <p
        className="text-sm leading-relaxed text-ink/85"
        data-testid={`suggestion-action-${index}`}
      >
        {card.action}
      </p>
      {card.evidence_note && <p className="text-xs text-ink/60">{card.evidence_note}</p>}
      {Array.isArray(card.evidence_sources) && (
        <ul className="space-y-1 text-xs" aria-label="Pairing sources">
          {card.evidence_sources.filter((source) => /^https:\/\/(www\.)?(foodbusinessnews\.net|lapanzanella\.com|effieshomemade\.com|presidentcheese\.com)\//.test(source.url || "")).map((source) => (
            <li key={source.url}><a href={source.url} target="_blank" rel="noopener noreferrer" className="underline">{source.title}</a></li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default SuggestionCard;
